// What the document's live editor adds on the screen, in one place: the `/`
// menu, the `[[` list, reference chips, the block handle, the reading state on
// phones, the empty-body placeholder, the editor props (paste, clicks, drags)
// and the menus drawn beside the editor (formatting bar, block menu). A note
// field (a Case's context) takes the same without the block handle.
import type { QueryClient } from "@tanstack/react-query";
import type { AnyExtension, Editor, EditorOptions } from "@tiptap/core";
import { Placeholder } from "@tiptap/extensions";
import { m } from "@trameria/i18n";
import type { FavoriteKind } from "../../app/screen-part";
import type { Api } from "../../data/api";
import { handleRefPaste } from "../../refs";
import {
  type DocumentBlockPart,
  displayBlockViews,
  pickFiles,
} from "../blocks";
import { BlockMenu } from "./block-menu";
import {
  bindRowResolution,
  TrameriaDragHandle,
  trameriaDragCopies,
} from "./drag-handle";
import { LinkSuggest } from "./link-suggest";
import { chipMouseDownWhileReading, TrameriaLinkWithChip } from "./ref-view";
import {
  linkClickWhileReading,
  linkMouseDownWhileReading,
  openLinkOnModClick,
  SelectionMenu,
} from "./selection-menu";
import { SlashMenu } from "./slash-menu";
import { TapToType } from "./tap-to-type";

export interface EditorKitContext {
  api: Api;
  qc: QueryClient;
  /** How each kind is drawn and opened (the parts' registrations). */
  kinds: Readonly<Record<string, FavoriteKind>>;
  /** The document being written (not offered by `[[`). */
  currentId(): string | null;
  /** The document body's parts (block-parts.ts); a note field has none. */
  parts?: readonly DocumentBlockPart[];
}

/**
 * The footnote dialog's editor: the body's look and the `/` menu, without what
 * makes no sense inside a footnote (another footnote, files: the dialog's
 * editor has no upload).
 */
export function footnoteDialogExtensions(): AnyExtension[] {
  return [
    ...displayBlockViews(),
    SlashMenu.configure({ exclude: ["footnote", "file"] }),
  ];
}

/** Replacements of the schema's own extensions (same names): given to `createEditorExtensions`. */
export function documentNodeViews(): AnyExtension[] {
  return [TrameriaLinkWithChip];
}

/** Extensions only the screen's document editor has. */
export function documentEditorExtensions(
  ctx: EditorKitContext,
): AnyExtension[] {
  return [
    // Files go through the body's upload (blocks/file-upload.ts); the parts
    // add theirs (an interactive UI block).
    SlashMenu.configure({
      onInsertFile: (editor, range) => void pickFiles(editor, range),
      items: (ctx.parts ?? []).flatMap((p) => p.slashItems ?? []),
    }),
    LinkSuggest.configure({
      context: { api: ctx.api, qc: ctx.qc, currentId: ctx.currentId },
    }),
    TrameriaDragHandle,
    TapToType,
    Placeholder.configure({
      // Only an empty document says it, on its only line.
      placeholder: ({ editor }) =>
        editor.state.doc.childCount > 1 ? "" : m.documents_body_placeholder(),
      showOnlyCurrent: false,
    }),
  ];
}

/**
 * Extensions of a note field's live editor (a Case's context): the document
 * editor's `/` menu, `[[` list, reading state and placeholder, without the
 * block handle (a note's blocks have no ids to drag by).
 */
export function noteEditorExtensions(
  ctx: EditorKitContext,
  options: { placeholder: () => string },
): AnyExtension[] {
  return [
    SlashMenu.configure({
      onInsertFile: (editor, range) => void pickFiles(editor, range),
    }),
    LinkSuggest.configure({
      context: { api: ctx.api, qc: ctx.qc, currentId: ctx.currentId },
    }),
    TapToType,
    Placeholder.configure({
      placeholder: ({ editor }) =>
        editor.state.doc.childCount > 1 ? "" : options.placeholder(),
      showOnlyCurrent: false,
    }),
  ];
}

/** Editor props of the document editor. */
export function documentEditorProps(
  ctx: EditorKitContext,
): NonNullable<EditorOptions["editorProps"]> {
  return {
    // Named for assistive technology (a field with a note profile names
    // itself instead, live-note.tsx).
    attributes: {
      role: "textbox",
      "aria-multiline": "true",
      "aria-label": m.documents_body(),
    },
    // A URL of this app pasted alone becomes a reference.
    handlePaste: (view, event) =>
      handleRefPaste(view, event, {
        kinds: ctx.kinds,
        qc: ctx.qc,
        api: ctx.api,
      }),
    // A drag from the handle is always a move (drag-handle.ts).
    dragCopies: trameriaDragCopies,
    // While writing, links open with Cmd / Ctrl + click; a plain click shows the card.
    handleClick: (_view, _pos, event) => openLinkOnModClick(event),
    // While reading, links and chips open on a plain click, without the caret.
    handleDOMEvents: {
      mousedown: (view, event) =>
        chipMouseDownWhileReading(view.hasFocus(), event) ||
        linkMouseDownWhileReading(view.hasFocus(), event),
      click: (view, event) => linkClickWhileReading(view.hasFocus(), event),
    },
  };
}

/** Called once when the editor is made. */
export function onDocumentEditorCreate(editor: Editor): void {
  bindRowResolution(editor);
}

/** The menus drawn next to the editor (fixed, inside the document area). */
export function DocumentEditorMenus({
  editor,
  parts,
}: {
  editor: Editor;
  parts?: readonly DocumentBlockPart[];
}) {
  return (
    <>
      <BlockMenu editor={editor} parts={parts} />
      <SelectionMenu editor={editor} />
    </>
  );
}

/** The menu drawn next to a note field's editor (no block menu: no block handle). */
export function NoteEditorMenus({ editor }: { editor: Editor }) {
  return <SelectionMenu editor={editor} />;
}
